// Hidden Income Authorization Service
import api from "./api";

export interface HiddenIncomeAuth {
  id: string;
  bookingRef: string;
  incomeType: string;
  amount: number;
  collectedAmount: number;
  pendingAmount: number;
  description: string;
  customerName: string;
  unitRef: string;
  incomeDate: string;
  isApproved: boolean;
  createdAt: string;
  createdBy: string;
  projectId: number | null;
  projectName: string | null;
}

export interface HiddenIncomeAuthResponse {
  success: boolean;
  data: HiddenIncomeAuth[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  emergency?: boolean;
  message?: string;
}

export interface ApproveHiddenIncomeResponse {
  success: boolean;
  message: string;
  approvedCount: number;
}

export interface RejectHiddenIncomeResponse {
  success: boolean;
  message: string;
  rejectedItem: {
    id: string;
    rejectionReason: string;
  };
}

class HiddenIncomeAuthService {
  private baseUrl = "/hidden-income/authorization";

  // Get pending hidden incomes for authorization
  async getPendingHiddenIncomes(
    page: number = 1,
    limit: number = 20,
    filters?: {
      customer?: string;
      unit?: string;
      bookingRef?: string;
      incomeType?: string;
      dateFrom?: string;
      dateTo?: string;
      projectId?: number | string;
    }
  ): Promise<HiddenIncomeAuthResponse> {
    try {
      const params: any = { page, limit };
      
      // Add filter parameters if provided
      if (filters?.customer) params.customer = filters.customer;
      if (filters?.unit) params.unit = filters.unit;
      if (filters?.bookingRef) params.bookingRef = filters.bookingRef;
      if (filters?.incomeType) params.incomeType = filters.incomeType;
      if (filters?.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters?.dateTo) params.dateTo = filters.dateTo;
      if (filters?.projectId) params.projectId = filters.projectId;

      const response = await api.get(`${this.baseUrl}/pending`, {
        params,
      });
      return response.data;
    } catch (error: any) {
      console.error("Error fetching pending hidden incomes:", error);
      throw new Error(
        error.response?.data?.message ||
          "Failed to fetch pending hidden incomes"
      );
    }
  }

  // Approve multiple hidden incomes
  async approveHiddenIncomes(
    ids: string[],
    approvalDate?: string
  ): Promise<ApproveHiddenIncomeResponse> {
    try {
      const response = await api.post(`${this.baseUrl}/approve`, {
        ids,
        approvalDate,
      });
      return response.data;
    } catch (error: any) {
      console.error("Error approving hidden incomes:", error);
      throw new Error(
        error.response?.data?.message || "Failed to approve hidden incomes"
      );
    }
  }

  // Reject hidden income
  async rejectHiddenIncome(
    id: string,
    rejectionReason: string
  ): Promise<RejectHiddenIncomeResponse> {
    try {
      const response = await api.delete(`${this.baseUrl}/${id}/reject`, {
        data: { rejectionReason },
      });
      return response.data;
    } catch (error: any) {
      console.error("Error rejecting hidden income:", error);
      throw new Error(
        error.response?.data?.message || "Failed to reject hidden income"
      );
    }
  }

  // Helper function to format amount display
  formatAmount(amount: number): string {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  // Helper function to format date display
  formatDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch (error) {
      return dateString;
    }
  }

  // Helper function to format date with 12-hour time (e.g. 21/01/2026 12:34 PM)
  formatDateTime12h(dateString: string): string {
    try {
      const date = new Date(dateString);
      const datePart = date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
      let timePart = date.toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
      // Ensure AM/PM are uppercase
      timePart = timePart.replace("am", "AM").replace("pm", "PM");
      return `${datePart} ${timePart}`;
    } catch (error) {
      return dateString;
    }
  }

  // Helper function to get income type display
  getIncomeTypeDisplay(incomeType: string): string {
    const typeMap: { [key: string]: string } = {
      booking_amount: "Booking Amount",
      agreement_amount: "Agreement Amount",
      extra_work: "Extra Work",
      other: "Other",
      other_work: "Other Work",
      terrace_work: "Terrace Work",
      charges_work: "Charges",
      banakhat_work: "Banakhat",
      adani_gas: "Adani Gas",
      adani_gas_work: "Adani Gas",
    };
    return typeMap[incomeType] || incomeType;
  }
}

export const hiddenIncomeAuthService = new HiddenIncomeAuthService();
export default hiddenIncomeAuthService;
